// src/components/pages/machinery/CargadoresFrontales.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    CargadoresFrontales_1,
    CargadoresFrontales_2,
    CargadoresFrontales_3,
    CargadoresFrontales_4,
    CargadoresFrontales_5,
    CargadoresFrontales_6
} from '../../../assets/images';
import { useCart } from '../../../context/CartContext';
import { jcb } from '../../../api/apiClient';

const SLUG = 'cargador-frontal';
const MACHINERY_TYPE = 'CARGADOR FRONTAL';

const CargadoresFrontales = () => {
    const { addMachinery, cartItems } = useCart();
    const navigate = useNavigate();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const [models, setModels] = useState([]);
    const [loadingModels, setLoadingModels] = useState(true);

    const images = [
        CargadoresFrontales_1,
        CargadoresFrontales_6,
        CargadoresFrontales_2,
        CargadoresFrontales_3,
        CargadoresFrontales_4,
        CargadoresFrontales_5,
    ];

    useEffect(() => {
        let isMounted = true;
        const loadModels = async () => {
            try {
                const { data } = await jcb.get(`/machinery/type/${SLUG}`);
                if (!isMounted) return;
                const mapped = (data.models || []).map((m) => ({
                    id: m.id,
                    name: m.name,
                    fullName: m.full_name || m.name,
                    weight: m.specs?.weight || '—',
                    power: m.specs?.engine_power || '—',
                    bucketCapacity: m.specs?.bucket_capacity || '—',
                    payload: m.specs?.lifting_capacity || '—',
                    photo: m.photo,
                }));
                setModels(mapped);
            } catch (err) {
                console.error('Error cargando modelos:', err);
            } finally {
                if (isMounted) setLoadingModels(false);
            }
        };
        loadModels();
        return () => { isMounted = false; };
    }, []);

    const nextImage = () => setCurrentImageIndex((p) => p === images.length - 1 ? 0 : p + 1);
    const prevImage = () => setCurrentImageIndex((p) => p === 0 ? images.length - 1 : p - 1);

    const features = [
        { title: 'Altamente Productiva', description: 'Sistema hidráulico de bomba doble con pistón de desplazamiento variable que circula 226 l/min para ciclos rápidos y excelente capacidad multifunción.' },
        { title: 'Motor DIESELMAX 672', description: 'Motor de 7.2 litros y 6 cilindros con sistema de combustión EcoMAX para máxima eficiencia y emisiones reducidas.' },
        { title: 'Geometría de Barra Z', description: 'Probada geometría que logra productividad excepcional con completa penetración en el montón y alta fuerza de rompimiento.' },
        { title: 'Comodidad para el Operador', description: 'Columna de dirección telescópica, aire acondicionado estándar y ventanas deslizantes para máxima ventilación.' },
        { title: 'Visibilidad Superior', description: 'Pantalla frontal laminada de tres piezas, espejos interiores y cámara de retroceso opcional para excelente visibilidad.' },
        { title: 'Durabilidad y Confiabilidad', description: 'Motor, ejes, transmisión y sistema hidráulico diseñados para funcionar en perfecta armonía y máxima eficacia.' }
    ];

    // ============================================================
    // Cart items (itemType: 'machinery')
    // ============================================================
    const buildModelCartItem = (model) => ({
        id: `machinery-${model.id}`,
        name: model.name,
        price: 0,
        machineryId: model.id,
        machineryType: MACHINERY_TYPE,
        model: model.name,
    });

    const buildGenericCartItem = () => ({
        id: `machinery-type-${SLUG}`,
        name: null,
        price: 0,
        machineryId: null,
        machineryType: MACHINERY_TYPE,
        model: null,
    });

    const handleAddToCart = (model) => addMachinery(buildModelCartItem(model));

    const handleAddAndGoToContact = (model) => {
        addMachinery(buildModelCartItem(model));
        navigate('/contact');
    };

    const handleAddGeneric = () => addMachinery(buildGenericCartItem());

    const handleAddGenericAndGoToContact = () => {
        handleAddGeneric();
        navigate('/contact');
    };

    const handleCtaQuote = () => handleAddGenericAndGoToContact();

    const isInCart = (id) =>
        cartItems.some(item => item.itemType === 'machinery' && item.id === `machinery-${id}`);

    const machineryCount = cartItems.filter(i => i.itemType === 'machinery').length;
    const ctaDisabled = loadingModels;
    const showModelsSection = !loadingModels && models.length > 0;

    return (
        <div className="container mx-auto px-6 py-24">
            <nav className="text-sm mb-8">
                <Link to="/" className="text-text-muted hover:text-dark-primary">Inicio</Link>
                <span className="mx-2 text-text-muted">/</span>
                <Link to="/machinery" className="text-text-muted hover:text-dark-primary">Maquinaria</Link>
                <span className="mx-2 text-text-muted">/</span>
                <span className="text-dark-primary font-medium">Cargadores Frontales</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
                <div>
                    <h1 className="text-4xl md:text-5xl font-display font-bold text-dark-primary mb-2">
                        Cargadores Frontales
                    </h1>
                    <p className="text-text-muted text-lg mb-6">
                        Nuestra gama de cargadores frontales ha sido creada teniendo en cuenta el feedback de nuestros clientes
                        sobre seguridad, eficiencia, facilidad de uso, mantenimiento, productividad, rendimiento, confiabilidad y comodidad.
                    </p>
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={handleCtaQuote}
                            disabled={ctaDisabled}
                            className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium bg-jcb-yellow text-dark-primary hover:bg-yellow-500 px-8 py-3 rounded-none transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Solicitar información
                        </button>
                    </div>
                </div>
                <div className="relative">
                    <div className="bg-surface-subtle aspect-video rounded-lg flex items-center justify-center overflow-hidden relative">
                        <img src={images[currentImageIndex]} alt={`Cargador Frontal - Imagen ${currentImageIndex + 1}`} className="w-full h-full object-cover transition-opacity duration-300" />
                        {images.length > 1 && (
                            <>
                                <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors" aria-label="Imagen anterior">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                </button>
                                <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors" aria-label="Imagen siguiente">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </button>
                                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                                    {images.map((_, index) => (
                                        <button key={index} onClick={() => setCurrentImageIndex(index)} className={`w-2 h-2 rounded-full transition-all ${currentImageIndex === index ? 'bg-white w-4' : 'bg-white/50'}`} aria-label={`Ir a imagen ${index + 1}`} />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    {images.length > 1 && (
                        <p className="text-center text-sm text-text-muted mt-2">{currentImageIndex + 1} / {images.length}</p>
                    )}
                </div>
            </div>

            <div className="mb-16 bg-surface-subtle p-8 md:p-12">
                <h2 className="text-2xl font-display font-bold text-dark-primary mb-6">Diseñados para Durar</h2>
                <p className="text-text-muted mb-4">
                    Ofrecemos una gran variedad de configuraciones para adecuar la maquinaria a sus requisitos con precisión.
                    En términos de rendimiento, el valor de los Cargadores Frontales JCB está en su gran poder, alto torque,
                    alta relación de potencia y peso, potente fuerza de arranque del balde y excelente tracción.
                </p>
                <p className="text-text-muted">
                    El nuevo JCB 455ZX está diseñado para mover más material por menos. Para lograr esto, hemos fabricado
                    el motor, los ejes, transmisión y el sistema hidráulico para que funcionen en perfecta armonía entre
                    ellos para lograr la máxima eficacia.
                </p>
            </div>

            <div className="mb-16">
                <h2 className="text-2xl font-display font-bold text-dark-primary mb-8">Características Destacadas</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((feature, index) => (
                        <div key={index} className="p-6 bg-white border border-border-subtle hover:shadow-md transition-shadow">
                            <h3 className="font-display font-semibold text-dark-primary text-lg mb-2">{feature.title}</h3>
                            <p className="text-text-muted text-sm">{feature.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {showModelsSection && (
                <div className="mb-16 overflow-x-auto">
                    <h2 className="text-2xl font-display font-bold text-dark-primary mb-8">Comparativa de Modelos</h2>

                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-dark-primary text-white">
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Modelo</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Peso</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Potencia</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Capacidad de balde</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Carga útil</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {models.map((model) => {
                                const inCart = isInCart(model.id);
                                return (
                                    <tr key={model.id} className="border-b border-border-subtle hover:bg-surface-subtle">
                                        <td className="px-6 py-4 font-display font-semibold text-dark-primary">{model.name}</td>
                                        <td className="px-6 py-4 text-text-muted">{model.weight}</td>
                                        <td className="px-6 py-4 text-text-muted">{model.power}</td>
                                        <td className="px-6 py-4 text-text-muted">{model.bucketCapacity}</td>
                                        <td className="px-6 py-4 text-text-muted">{model.payload}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <button onClick={() => handleAddToCart(model)} className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors whitespace-nowrap">
                                                    {inCart ? 'Agregar +1' : 'Agregar a cotización'}
                                                </button>
                                                <span className="text-border-subtle hidden sm:inline">|</span>
                                                {inCart ? (
                                                    <Link to="/cart" className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors whitespace-nowrap">
                                                        Ver carrito
                                                    </Link>
                                                ) : (
                                                    <button onClick={() => handleAddAndGoToContact(model)} className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors whitespace-nowrap">
                                                        Cotizar
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {machineryCount > 0 && (
                        <div className="mt-4 flex justify-end">
                            <Link to="/cart" className="font-display text-xs uppercase tracking-luxury font-medium bg-dark-primary text-white hover:bg-black px-6 py-3 transition-all">
                                Ir a cotizar ({machineryCount} {machineryCount === 1 ? 'equipo' : 'equipos'})
                            </Link>
                        </div>
                    )}
                </div>
            )}

            <div className="bg-surface-subtle p-8 md:p-12 text-center">
                <h2 className="text-2xl font-display font-bold text-dark-primary mb-4">¿Interesado en los Cargadores Frontales?</h2>
                <p className="text-text-muted mb-6 max-w-2xl mx-auto">
                    Contáctanos para obtener más información, cotizaciones o asesoría técnica sobre nuestros modelos.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                    <Link to="/contact" className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium border border-border-medium text-text-muted hover:text-dark-primary hover:border-dark-primary px-8 py-3 transition-all duration-300">
                        Contactar
                    </Link>
                    <button onClick={handleCtaQuote} disabled={ctaDisabled} className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium bg-jcb-yellow text-dark-primary hover:bg-yellow-500 px-8 py-3 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                        Solicitar información
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CargadoresFrontales;